import { useEffect, useCallback, Fragment } from 'react';
import { Box, Divider, Link, Skeleton, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import {
  getRouterPathToArticle,
  getRouterPathToAuthor,
} from '../../utilities/Links';
import { getFormattedSearchValue, minSearchLength } from './SearchUtils';
import { addEventToGTMDataLayer } from '../../utilities/gtm';
import { useSearchArticles } from '../../hooks/useSearchArticles';
import ArticleMeta from '../../types/ArticleMeta';
import ArticleCoverImage from '../Article/ArticleCoverImage';
import { debounceDelay } from './constants';
import { useDebounce } from '../../hooks/useDebounce';

interface ArticleResultProps {
  readonly article: ArticleMeta;
}

function ArticleResult({ article }: ArticleResultProps) {
  const { t } = useTranslation();
  const isNews = article.type === 'Uutinen';

  return (
    <Stack
      component='article'
      direction='row'
      useFlexGap
      sx={{
        gap: 3,
        alignItems: 'center',
      }}
    >
      <Box sx={{ flexShrink: 0, width: '80px' }}>
        <ArticleCoverImage
          category={article.categories.primary}
          src={article.coverImage}
        />
      </Box>
      <Stack
        sx={{
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <Typography
          variant='overline'
          sx={{
            color: 'text.secondary',
          }}
        >
          {isNews ? t('contentType.Uutinen') : article.type}
        </Typography>
        <Link
          component={RouterLink}
          variant='h6'
          noWrap
          underline='hover'
          to={getRouterPathToArticle(article.id)}
          sx={{
            color: 'text.primary',
          }}
        >
          {article.title}
        </Link>
        <Stack
          direction='row'
          useFlexGap
          sx={{
            alignItems: 'baseline',
            gap: 2,
          }}
        >
          {!isNews && article.authors.length > 0 && (
            <Stack direction='row'>
              {article.authors.map((author, i) => [
                i > 0 && (
                  <Typography
                    key={'author-list-comma-' + i}
                    component='span'
                    variant='caption'
                    sx={{
                      color: 'primary.main',
                      marginRight: i > 0 ? 0.5 : 0,
                    }}
                  >
                    ,
                  </Typography>
                ),
                <Link
                  key={'author-result-' + author}
                  component={RouterLink}
                  to={getRouterPathToAuthor(author)}
                  underline='hover'
                  variant='caption'
                  sx={{
                    color: 'primary.main',
                    display: 'block',
                    flexWrap: 'nowrap',
                  }}
                >
                  {author}
                </Link>,
              ])}
            </Stack>
          )}
          <Typography
            variant='caption'
            sx={{
              color: 'text.secondary',
            }}
          >
            {dayjs(article.releaseDate).format('D.M.YYYY')}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}

function ArticleResultSkeleton() {
  return (
    <Stack
      direction='row'
      useFlexGap
      sx={{
        gap: 3,
        alignItems: 'center',
      }}
    >
      <Skeleton
        variant='rectangular'
        height='60px'
        sx={{ aspectRatio: 80 / 60 }}
      />
      <Stack
        useFlexGap
        sx={{
          width: '100%',
          gap: 1,
        }}
      >
        <Skeleton variant='rectangular' height='18px' />
        <Skeleton variant='rectangular' height='14px' />
        <Skeleton variant='rectangular' height='14px' />
      </Stack>
    </Stack>
  );
}

interface ContentSearchResultsListProps {
  readonly search: string;
}

export default function ContentSearchResultsList({
  search,
}: ContentSearchResultsListProps) {
  const { t } = useTranslation();
  const debouncedSearch = useDebounce(search, debounceDelay);

  const { articles, totalArticleCount, isFetching, status } = useSearchArticles(
    {
      minSearchLength,
      search: debouncedSearch,
      itemsPerPage: 10,
    },
  );

  const handleSearchEvent = useCallback(
    (count: number) => {
      addEventToGTMDataLayer({
        event: 'search',
        search_term: search,
        formatted_search_term: getFormattedSearchValue(search),
        search_results_count: count,
      });
    },
    [search],
  );

  useEffect(() => {
    if (status === 'success') {
      handleSearchEvent(totalArticleCount);
    }
  }, [status, totalArticleCount, handleSearchEvent]);

  const isLoading = isFetching || status === 'pending';

  return (
    <>
      {isLoading && (
        <Stack>
          <Typography
            component='p'
            variant='h5'
            sx={{
              paddingBottom: 1,
            }}
          >
            Haetaan sisältöjä...
          </Typography>
          <Stack
            useFlexGap
            sx={{
              gap: 1,
            }}
          >
            {[0, 1, 2, 3, 4].map((index) => (
              <Fragment key={`skeleton-${index}`}>
                <ArticleResultSkeleton />
                {index < 4 && <Divider sx={{ marginLeft: 13 }} />}
              </Fragment>
            ))}
          </Stack>
        </Stack>
      )}
      {!isLoading && (
        <>
          {articles.length === 0 && (
            <Box>
              <Typography variant='h5' component='h2'>
                {t('search.noResultsTitle')}
              </Typography>
              <Typography variant='body1'>
                {t('search.noResultsHelper')}
              </Typography>
            </Box>
          )}
          {articles.length > 0 && (
            <>
              <Stack
                direction='row'
                useFlexGap
                sx={{
                  gap: 1,
                }}
              >
                <Typography
                  component='h2'
                  variant='h5'
                  sx={{
                    color: 'text.secondary',
                    paddingBottom: 1,
                  }}
                >
                  {t('search.contentAndNews')}
                </Typography>
              </Stack>
              <Stack
                useFlexGap
                sx={{
                  gap: 1,
                }}
              >
                {articles.slice(0, 5).map((article, index) => (
                  <Fragment key={'article-result-' + article.id}>
                    <ArticleResult article={article} />
                    {index < articles.slice(0, 5).length - 1 && (
                      <Divider sx={{ marginLeft: 13 }} />
                    )}
                  </Fragment>
                ))}
              </Stack>
            </>
          )}
        </>
      )}
    </>
  );
}
